/**
 * calendar/slot-hint.js — the "Book 9:30 AM" marker over the time slot under the pointer.
 *
 * Owned here once for both scheduling surfaces: the calendar's week/day time grid
 * (core.js) and the Dispatch board (dispatch_controller.js). Each surface knows
 * how to turn a pointer into a slot; this module owns how the slot is drawn and worded.
 *
 * The hint is a CHILD of the element that scrolls with the grid (a FullCalendar column
 * frame, a board lane) and is positioned in that parent's own coordinates. That is what
 * keeps it pinned to its slot while the page or the grid scrolls, and lets the parent's
 * stacking order put it under event chips. Never position it from viewport rects.
 *
 * Imports nothing, so it adds no edge to the calendar import graph.
 */

/** "9:30 AM" for minutes after midnight. */
export function clockLabel(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = String(minutes % 60).padStart(2, '0');
    return `${hours % 12 || 12}:${mins} ${hours < 12 ? 'AM' : 'PM'}`;
}

/**
 * One reusable hint element. show() moves it under `parent` (a positioned element),
 * hide() detaches it; the element survives FullCalendar and Turbo re-renders because
 * it is only ever attached while the pointer is over a live parent.
 *
 * `variant` adds slot-hint-<variant>: 'selected' is the slot a tap has picked, drawn
 * solid while its booking modal is open, as against the dashed slot a mouse is over.
 */
export function createSlotHint(variant = '') {
    const el = document.createElement('div');
    el.className = variant ? `slot-hint slot-hint-${variant}` : 'slot-hint';
    return {
        /** @param {number} offset px from the parent's start edge along the time axis
         *  @param {number} size   px, one slot
         *  @param {boolean} across time runs left to right (the Dispatch board's
         *         default layout) rather than top to bottom */
        show(parent, offset, size, text, across = false) {
            el.classList.toggle('slot-hint-across', across);
            el.style.top = across ? '' : `${offset}px`;
            el.style.height = across ? '' : `${size}px`;
            el.style.left = across ? `${offset}px` : '';
            el.style.width = across ? `${size}px` : '';
            el.textContent = text;
            if (el.parentElement !== parent) parent.appendChild(el);
        },
        hide() {
            el.remove();
        },
        isShown() {
            return el.isConnected;
        }
    };
}
